import moment from "moment-timezone";
import React, { useEffect, useRef } from "react";
import { FixedSizeList } from "react-window";
import { useReactiveVar } from "@apollo/client";
import { remove } from "lodash";
import { currentSport } from "src/services/ApolloService";
import AtsCell from "./cells/AtsCell";
import B2BCell from "./cells/B2BCell";
import OpponentCell from "./cells/OpponentCell";
import OuCell from "./cells/OuCell";
import ScoreCell from "./cells/ScoreCell";

const ScheduleTable = ({
  filteredSchedule,
  freeGame,
  team,
  hasFilter,
}: {
  filteredSchedule: any;
  freeGame: boolean;
  team: any;
  hasFilter: boolean;
}) => {
  const ref = useRef<any>();

  useEffect(() => {
    if (ref.current) {
      // since the array is in ascending order, find the first game after today!
      const index = filteredSchedule.findIndex((event: any) => {
        return moment(event.startDate).isAfter();
      });

      if (index < 0) {
        return ref.current.scrollToItem(filteredSchedule.length - 1);
      }
      // User can see today’s game on the schedule after the 3 most recent games
      return ref.current.scrollToItem(index + 5);
    }
  }, [filteredSchedule]);

  let modifiedFilteredSchedule = remove([...filteredSchedule], function (Obj) {
    return Obj.awayTeam.fullName !== "BYE"; // ignore BYE events
  });

  const homeOrAway = team.id === filteredSchedule[0]?.homeTeam?.id ? "home" : "away"; // prettier-ignore

  // Insert the teamStats.byeWeek into the schedule
  let indexOfByeWeek: number = 0;
  let byeWeek: string = modifiedFilteredSchedule[0]?.[`${homeOrAway}Team`].teamStats.byeWeek; // prettier-ignore
  if (!hasFilter && byeWeek) {
    modifiedFilteredSchedule.forEach((game, index) => {
      if (moment(game.startDate).isSameOrBefore(byeWeek)) {
        indexOfByeWeek = index;
      }
    });
    modifiedFilteredSchedule.splice(indexOfByeWeek + 1, 0, "byeWeek");
  }

  return (
    <FixedSizeList
      ref={ref}
      height={495}
      width="100%"
      itemData={{
        modifiedFilteredSchedule,
        freeGame,
        team,
        byeWeek,
      }}
      className="schedule-list__overflow-right-list"
      itemCount={modifiedFilteredSchedule.length}
      itemSize={55}
    >
      {ScheduleItem}
    </FixedSizeList>
  );
};
// Render list of table rows

const getMatchupName = (weekId: number) => {
  switch (weekId) {
    case 19:
      return "WC";
    case 20:
      return "Div";
    case 21:
      return "CC";
    case 22:
      return "SB";
  }
};

const ScheduleItem = ({ data, index, style }: any) => {
  const { freeGame, modifiedFilteredSchedule, team, byeWeek } = data;

  const scheduleItem = modifiedFilteredSchedule[index];
  const isByeWeek = scheduleItem === "byeWeek";
  const startDate = moment(scheduleItem?.startDate).format("ddd M/D");
  const isHome = scheduleItem?.homeTeam?.id === team.id;
  const league = useReactiveVar(currentSport);
  const displayB2BColumn = league?.sport.name !== "Soccer" && league?.sport.name !== "Football";
  const displayWeekColumn = league?.name === "NFL";
  const numberOFColumns = () => {
    if (displayB2BColumn || displayWeekColumn) {
      return "columns-6";
    }
    return "columns-5";
  };

  return (
    <div className={`schedule-list__row ${numberOFColumns()}`} key={index} style={style}>
      {displayWeekColumn ? (
        <span className="schedule-list__table-cell">
          {isByeWeek
            ? modifiedFilteredSchedule[index - 1].matchup?.weekId + 1
            : scheduleItem.matchup?.seasonType === "POSTSEASON"
            ? getMatchupName(scheduleItem?.matchup?.weekId)
            : scheduleItem?.matchup?.weekId}
        </span>
      ) : null}

      <span className="schedule-list__table-cell">{isByeWeek ? moment.utc(byeWeek).format("M/D") : startDate}</span>

      {isByeWeek ? (
        <span className="schedule-list__table-cell">
          <span className="schedule-list__opp_container">BYE WEEK</span>
        </span>
      ) : (
        <OpponentCell scheduleItem={scheduleItem} isHome={isHome} />
      )}

      {isByeWeek ? (
        <span className="schedule-list__table-cell">--</span>
      ) : (
        <ScoreCell scheduleItem={scheduleItem} isHome={isHome} />
      )}

      {isByeWeek ? (
        <span className="schedule-list__table-cell">--</span>
      ) : (
        <AtsCell freeGame={freeGame} isHome={isHome} scheduleItem={scheduleItem} />
      )}

      {isByeWeek ? (
        <span className="schedule-list__table-cell">--</span>
      ) : (
        <OuCell freeGame={freeGame} isHome={isHome} scheduleItem={scheduleItem} />
      )}

      {displayB2BColumn && (
        <B2BCell filteredSchedule={modifiedFilteredSchedule} scheduleItem={scheduleItem} freeGame={freeGame} />
      )}
    </div>
  );
};

export default ScheduleTable;
